import { GetServerSidePropsContext, NextPage } from 'next'
import useHeader from 'src/relay/Header/useHeader'
import useFooter from 'src/relay/Footer/useFooter'
import Blocks from 'src/relay/Blocks'
import useBreadcrumb from 'src/relay/Breadcrumb/useBreadcrumb'
import { Engagement } from 'src/rest/types/Engagement'
import GetEngagementBySlug from 'src/rest/requests/engagements/getEngagementBySlug'
import useLangSwitch from 'src/relay/LangSwitch/useLangSwitch'
import useHeroWithTag from 'src/relay/Hero/useHeroWithTag'

import { initWithLangAndLabels } from '../../i18n'
import PageTemplate from '../../templates/PageTemplate'
import {
  GetServerSidePropsReturnType,
  PageProps,
} from '../../relay/Common/PageProps'
import getConfiguration from '../../rest/services/getConfiguration'
import RestApiClient from '../../rest/client'
import { sanitizeSSRProps } from '../../utils/sanitizeSSRProps'
import { SeoTransformer } from '../../relay/Transformers/SeoTransformer'

type Props = PageProps & {
  data: Engagement
}

const Page: NextPage<Props> = ({
  data,
  configuration,
  locale: currentLocale,
}) => {
  const langSwitch = useLangSwitch({
    configuration,
    currentLocale,
    localized: data.localizations ?? [],
  })

  const header = useHeader({
    configuration,
    currentLocale,
    langSwitch,
  })
  const footer = useFooter({
    configuration,
    currentLocale,
    langSwitch,
    theme: data.theme,
  })
  const hero = useHeroWithTag({
    hero: data.hero,
    type: 'engagements',
    theme: data.theme,
  })
  const breadcrumb = useBreadcrumb({ page: data })

  return (
    <PageTemplate
      headerProps={header}
      footerProps={footer}
      hero={hero}
      breadcrumbProps={breadcrumb}
    >
      <Blocks
        blocks={data?.blocks ?? []}
        configuration={configuration}
        locale={currentLocale}
        pageInfos={data}
      />
    </PageTemplate>
  )
}

export default Page

export async function getServerSideProps(
  context: GetServerSidePropsContext
): GetServerSidePropsReturnType<Props> {
  const locale = context?.locale ?? 'fr'

  const slug: string =
    Array.isArray(context?.params?.slug) && context?.params?.slug
      ? (context.params.slug as string[]).join('/')
      : ((context?.params?.slug as string) ?? null)

  const [configuration, rs] = await Promise.all([
    getConfiguration(locale),
    RestApiClient.call(GetEngagementBySlug({ slug: slug, locale: locale })),
  ])

  const data = rs?.data ?? null
  const i18n = initWithLangAndLabels(locale, configuration.translations ?? {})

  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59'
  )

  if (!data) {
    return {
      notFound: true,
      props: {
        i18n,
        configuration,
      },
    }
  }

  return {
    props: {
      metas: SeoTransformer(context.resolvedUrl, data?.seo, locale),
      data: sanitizeSSRProps(data),
      configuration,
      i18n,
      locale,
    },
  }
}
